import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BarChart2, Plus, Trash2, X, DollarSign, TrendingUp } from 'lucide-react';
import { SwissCard, KpiCard, SectionHeader, EmptyState } from '../layout/SwissUI';

const API = process.env.REACT_APP_BACKEND_URL;
const f = (n) => `$${Number(n || 0).toLocaleString()}`;
const EVENT_TYPES = ['Wedding & Bridal', 'Celebration', 'Corporate & Travel', 'Entertainment', 'Memorial', 'Media & Production'];
const CATEGORIES = ['Exotic Driven', 'Concierge Driven', 'Group Driven'];

const VehiclePLTab = () => {
  const [data, setData] = useState({ vehicles: [], fleet_revenue: 0, monthly_costs: 0, net_profit: 0, top_earner: null });
  const [showForm, setShowForm] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [showRevForm, setShowRevForm] = useState(false);
  const [form, setForm] = useState({ vehicle_name: '', category: 'Exotic Driven', purchase_price: 0, monthly_note: 0, monthly_insurance: 0, monthly_maintenance: 0, fuel_est: 0, year_acquired: '' });
  const [revForm, setRevForm] = useState({ revenue: 0, date: new Date().toISOString().slice(0, 10), event_type: '', notes: '' });

  const load = () => { fetch(`${API}/api/command-center/vehicle-pl`).then(r => r.json()).then(setData); };
  useEffect(load, []);

  const create = async () => {
    if (!form.vehicle_name) return;
    await fetch(`${API}/api/command-center/vehicle-pl`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    setShowForm(false); load();
  };

  const logRevenue = async () => {
    if (!selectedId) return;
    await fetch(`${API}/api/command-center/vehicle-pl/${selectedId}/revenue`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(revForm) });
    setShowRevForm(false); setRevForm({ revenue: 0, date: new Date().toISOString().slice(0, 10), event_type: '', notes: '' }); load();
  };

  const del = async (id) => { if (!window.confirm('Delete this vehicle?')) return; await fetch(`${API}/api/command-center/vehicle-pl/${id}`, { method: 'DELETE' }); setSelectedId(null); load(); };

  const selected = data.vehicles.find(v => v.vehicle_pl_id === selectedId);
  const monthlyCost = (v) => (v?.monthly_note || 0) + (v?.monthly_insurance || 0) + (v?.monthly_maintenance || 0) + (v?.fuel_est || 0);

  return (
    <div data-testid="vehicle-pl-tab">
      <SectionHeader
        overline="Fleet · Economics"
        title={<span className="flex items-center gap-2"><BarChart2 className="h-5 w-5 text-[#E52B20]" /> Vehicle P&L</span>}
        action={
          <button onClick={() => setShowForm(true)} className="h-9 px-4 bg-[#E52B20] hover:bg-[#C92218] text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1" data-testid="add-vehicle-pl-btn">
            <Plus className="h-3 w-3" /> Add Vehicle
          </button>
        }
      />

      {showForm && (
        <SwissCard className="p-4 mb-4">
          <div className="flex justify-between mb-3"><h3 className="text-sm font-black uppercase tracking-wider">Add Vehicle</h3><button onClick={() => setShowForm(false)}><X className="h-4 w-4 text-[#666]" /></button></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Input placeholder="Vehicle Name" value={form.vehicle_name} onChange={e => setForm(p => ({ ...p, vehicle_name: e.target.value }))} className="h-9 text-xs" data-testid="vpl-vehicle-name" />
            <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}><SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
            <Input type="number" placeholder="Purchase Price $" value={form.purchase_price || ''} onChange={e => setForm(p => ({ ...p, purchase_price: parseFloat(e.target.value) || 0 }))} className="h-9 text-xs" />
            <Input type="number" placeholder="Monthly Note $" value={form.monthly_note || ''} onChange={e => setForm(p => ({ ...p, monthly_note: parseFloat(e.target.value) || 0 }))} className="h-9 text-xs" />
            <Input type="number" placeholder="Monthly Insurance $" value={form.monthly_insurance || ''} onChange={e => setForm(p => ({ ...p, monthly_insurance: parseFloat(e.target.value) || 0 }))} className="h-9 text-xs" />
            <Input type="number" placeholder="Monthly Maintenance $" value={form.monthly_maintenance || ''} onChange={e => setForm(p => ({ ...p, monthly_maintenance: parseFloat(e.target.value) || 0 }))} className="h-9 text-xs" />
            <Input type="number" placeholder="Fuel Est/Month $" value={form.fuel_est || ''} onChange={e => setForm(p => ({ ...p, fuel_est: parseFloat(e.target.value) || 0 }))} className="h-9 text-xs" />
            <Input placeholder="Year Acquired" value={form.year_acquired} onChange={e => setForm(p => ({ ...p, year_acquired: e.target.value }))} className="h-9 text-xs" />
          </div>
          <div className="flex gap-2 mt-3"><Button size="sm" onClick={create} className="bg-[#E52B20] hover:bg-[#C92218] text-xs h-8">Save Vehicle</Button><Button size="sm" variant="outline" onClick={() => setShowForm(false)} className="text-xs h-8">Cancel</Button></div>
        </SwissCard>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <KpiCard label="Fleet Revenue" value={f(data.fleet_revenue)} tone="success" testid="vpl-kpi-revenue" />
        <KpiCard label="Monthly Costs" value={f(data.monthly_costs)} tone="danger" testid="vpl-kpi-costs" />
        <KpiCard label="Net Profit" value={f(data.net_profit)} tone={data.net_profit >= 0 ? 'success' : 'danger'} testid="vpl-kpi-net" />
        <KpiCard label="Top Earner" value={data.top_earner || '—'} testid="vpl-kpi-top" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Vehicle List */}
        <div className="lg:col-span-2">
          {data.vehicles.length === 0 ? (
            <EmptyState icon={BarChart2} title="No vehicles added yet" description="Add a vehicle to start tracking its P&L." />
          ) : (
            <div className="space-y-2">
              {data.vehicles.map(v => {
                const mc = monthlyCost(v);
                const profit = (v.total_revenue || 0) - mc;
                return (
                  <SwissCard key={v.vehicle_pl_id} className={`p-3 cursor-pointer transition-all ${selectedId === v.vehicle_pl_id ? 'border-[#0A0A0A] shadow-[4px_4px_0px_#0A0A0A]' : 'hover:border-[#0A0A0A]'}`}>
                    <div onClick={() => setSelectedId(v.vehicle_pl_id)} className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-bold">{v.vehicle_name}</p>
                        <div className="flex gap-3 text-[10px] text-[#666]">
                          <span>{v.category}</span>
                          {v.year_acquired && <span>Since {v.year_acquired}</span>}
                          <span>Cost: {f(mc)}/mo</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm font-black font-mono text-[#039855]">{f(v.total_revenue || 0)}</p>
                          <p className={`text-[10px] font-bold font-mono ${profit >= 0 ? 'text-[#039855]' : 'text-[#E52B20]'}`}>Net: {f(profit)}</p>
                        </div>
                        <div className="text-[10px] text-[#666] font-mono">{(v.revenue_log || []).length} entries</div>
                      </div>
                    </div>
                  </SwissCard>
                );
              })}
            </div>
          )}
        </div>

        {/* Detail Panel */}
        <div>
          {selected ? (
            <Card className="p-4 border-gray-200">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-bold">{selected.vehicle_name}</h3>
                <div className="flex gap-1">
                  <Button size="sm" onClick={() => setShowRevForm(true)} className="bg-emerald-600 hover:bg-emerald-700 text-[10px] h-7 px-2"><Plus className="h-3 w-3 mr-0.5" /> Log Revenue</Button>
                  <Button size="sm" variant="outline" onClick={() => del(selected.vehicle_pl_id)} className="text-[10px] h-7 px-2 text-red-500"><Trash2 className="h-3 w-3" /></Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="p-2 bg-gray-50 rounded text-center"><p className="text-[9px] text-gray-500">Revenue</p><p className="text-sm font-bold text-emerald-600">{f(selected.total_revenue || 0)}</p></div>
                <div className="p-2 bg-gray-50 rounded text-center"><p className="text-[9px] text-gray-500">Monthly Cost</p><p className="text-sm font-bold text-red-600">{f(monthlyCost(selected))}</p></div>
              </div>

              <div className="space-y-1 text-[10px] mb-3">
                {selected.monthly_note > 0 && <div className="flex justify-between"><span className="text-gray-500">Note/Payment</span><span>{f(selected.monthly_note)}/mo</span></div>}
                {selected.monthly_insurance > 0 && <div className="flex justify-between"><span className="text-gray-500">Insurance</span><span>{f(selected.monthly_insurance)}/mo</span></div>}
                {selected.monthly_maintenance > 0 && <div className="flex justify-between"><span className="text-gray-500">Maintenance</span><span>{f(selected.monthly_maintenance)}/mo</span></div>}
                {selected.fuel_est > 0 && <div className="flex justify-between"><span className="text-gray-500">Fuel</span><span>{f(selected.fuel_est)}/mo</span></div>}
                {selected.purchase_price > 0 && <div className="flex justify-between pt-1 border-t border-gray-200"><span className="text-gray-500">Purchase Price</span><span className="font-semibold">{f(selected.purchase_price)}</span></div>}
              </div>

              {showRevForm && (
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 mb-3">
                  <h4 className="text-[10px] font-bold mb-2">Log Revenue</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="number" placeholder="Revenue $" value={revForm.revenue || ''} onChange={e => setRevForm(p => ({ ...p, revenue: parseFloat(e.target.value) || 0 }))} className="h-7 text-[10px]" />
                    <Input type="date" value={revForm.date} onChange={e => setRevForm(p => ({ ...p, date: e.target.value }))} className="h-7 text-[10px]" />
                    <Select value={revForm.event_type} onValueChange={v => setRevForm(p => ({ ...p, event_type: v }))}><SelectTrigger className="h-7 text-[10px]"><SelectValue placeholder="Event Type" /></SelectTrigger><SelectContent>{EVENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
                    <Input placeholder="Notes" value={revForm.notes} onChange={e => setRevForm(p => ({ ...p, notes: e.target.value }))} className="h-7 text-[10px]" />
                  </div>
                  <div className="flex gap-2 mt-2"><Button size="sm" onClick={logRevenue} className="bg-emerald-600 text-[10px] h-6 px-2">Log</Button><Button size="sm" variant="outline" onClick={() => setShowRevForm(false)} className="text-[10px] h-6 px-2">Cancel</Button></div>
                </div>
              )}

              <h4 className="text-[10px] font-bold text-gray-400 uppercase mb-2">Revenue Log</h4>
              {(selected.revenue_log || []).length === 0 ? (
                <p className="text-[10px] text-gray-400">No revenue entries.</p>
              ) : (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {(selected.revenue_log || []).slice().reverse().map((e, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-[10px]">
                      <div><span className="font-semibold">{e.date}</span> <span className="text-gray-400">{e.event_type}</span></div>
                      <span className="font-bold text-emerald-600">{f(e.revenue)}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ) : (
            <Card className="p-6 border-gray-200 text-center"><BarChart2 className="h-8 w-8 text-gray-300 mx-auto mb-2" /><p className="text-xs text-gray-400">Select a vehicle to view P&L details</p></Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehiclePLTab;
